/**
 * The Tunnel Shell (FS-03) — the scrolling synthwave tube the player flies down. Built once at boot
 * and scrolled by advancing a single offset; rings recycle by Z-modulo, never rebuilt.
 */
import * as THREE from 'three';
import { CULL_Z, TUNNEL_RADIUS } from '../core/constants';
import { PALETTE } from './palette';

const RING_COUNT = 40;
const RING_SPACING = 12; // u
const RING_SEGMENTS = 48;
const LONGITUDINAL = 16;

export class TunnelShell {
  readonly group = new THREE.Group();
  private rings: THREE.LineLoop[] = [];
  private length = RING_COUNT * RING_SPACING;
  private scroll = 0;

  constructor() {
    const circle: number[] = [];
    for (let i = 0; i <= RING_SEGMENTS; i++) {
      const a = (i / RING_SEGMENTS) * Math.PI * 2;
      circle.push(Math.sin(a) * TUNNEL_RADIUS, Math.cos(a) * TUNNEL_RADIUS, 0);
    }
    const ringGeo = new THREE.BufferGeometry();
    ringGeo.setAttribute('position', new THREE.Float32BufferAttribute(circle, 3));

    for (let k = 0; k < RING_COUNT; k++) {
      const mat = new THREE.LineBasicMaterial({
        color: k % 2 === 0 ? PALETTE.primary : PALETTE.secondary,
        transparent: true,
        opacity: 0.5,
      });
      const ring = new THREE.LineLoop(ringGeo, mat);
      ring.position.z = -this.length + k * RING_SPACING;
      this.rings.push(ring);
      this.group.add(ring);
    }

    // Longitudinal lines running down the tunnel.
    for (let j = 0; j < LONGITUDINAL; j++) {
      const a = (j / LONGITUDINAL) * Math.PI * 2;
      const x = Math.sin(a) * TUNNEL_RADIUS;
      const y = Math.cos(a) * TUNNEL_RADIUS;
      const geo = new THREE.BufferGeometry();
      geo.setAttribute(
        'position',
        new THREE.Float32BufferAttribute([x, y, -this.length, x, y, CULL_Z], 3),
      );
      const mat = new THREE.LineBasicMaterial({ color: PALETTE.grid, transparent: true, opacity: 0.35 });
      this.group.add(new THREE.Line(geo, mat));
    }
  }

  update(scrollSpeed: number, dt: number, overdrive: boolean): void {
    this.scroll += scrollSpeed * dt;
    const s = this.scroll % RING_SPACING;
    for (let k = 0; k < this.rings.length; k++) {
      const ring = this.rings[k]!;
      let z = -this.length + k * RING_SPACING + s;
      if (z > CULL_Z) z -= this.length;
      ring.position.z = z;
      // Brighten rings as they approach.
      const t = 1 - Math.min(1, -z / this.length);
      const mat = ring.material as THREE.LineBasicMaterial;
      mat.opacity = 0.15 + t * 0.6;
      if (overdrive) mat.color.copy(PALETTE.overdrive);
      else mat.color.copy(k % 2 === 0 ? PALETTE.primary : PALETTE.secondary);
    }
  }
}
